import { useEffect, useRef } from 'react';
import { optimizeHouseRoute } from '../services/firebase/FunctionsService';

type Opts = {
  /** True when departure is authorized and the runner is ready to collect. */
  enabled: boolean;
  houseId?: string;
  routeId?: string;
  /** Used to re-run once per authorization event. */
  authorizedAt?: number | null;
  online?: boolean;
};

/**
 * When an authorized route starts (runner or banker self-collect), reorder stops
 * from live GPS (house HQ stays last). Not a manual banker/god control.
 */
export function useAutoOptimizeRouteOnStart(opts: Opts) {
  const doneKey = useRef('');

  useEffect(() => {
    if (!opts.enabled || !opts.houseId || opts.online === false) return undefined;
    const key = `${opts.routeId ?? opts.houseId}:${opts.authorizedAt ?? 'auth'}`;
    if (doneKey.current === key) return undefined;

    let cancelled = false;

    const run = async (start?: { lat: number; lng: number } | null) => {
      if (cancelled || doneKey.current === key) return;
      doneKey.current = key;
      try {
        await optimizeHouseRoute({
          houseId: opts.houseId,
          ...(start
            ? { startLat: start.lat, startLng: start.lng }
            : {}),
        });
      } catch (err) {
        // Allow a retry on next mount / auth cycle.
        doneKey.current = '';
        console.warn('[Route] Auto-optimize failed:', err);
      }
    };

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      void run(null);
      return () => { cancelled = true; };
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void run({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        void run(null);
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 12_000 },
    );

    return () => { cancelled = true; };
  }, [opts.enabled, opts.houseId, opts.routeId, opts.authorizedAt, opts.online]);
}
